import { LitElement, html } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { customElement } from 'lit/decorators.js';
import { AtlFileUploadProps } from './file-upload.props.ts';

import * as TagNames from './file-upload.tagnames.ts';
import fileUploadStyles from './file-upload.scss?inline';
import { AtlFileUploadEvent } from './file-upload.events.ts';
import { enumAttributeConverter } from '../enum-types.ts';
import { ButtonSize, ButtonVariant } from '../button/button.enums.ts';
import publishIcon from '../../icons/publish-icon.svg?raw';
import { DragDropReader } from './drag-drop-reader.ts';

@customElement(TagNames.FileUpload)
export class AtlFileUpload extends LitElement implements AtlFileUploadProps {
   declare toggleLabel?: string;
   declare allowedFileExtensions?: string[];
   declare foldersAllowed?: boolean;
   declare size?: ButtonSize;
   declare variant?: ButtonVariant;
   declare fileUploadLabel?: string;
   declare folderUploadLabel?: string;
   declare disabled?: boolean;
   declare selectMultiple?: boolean;

   hoverClass = '';

   static properties = {
      toggleLabel: { type: String },
      allowedFileExtensions: { type: Array },
      hoverClass: { type: String },
      foldersAllowed: { type: Boolean },
      size: { type: String, converter: enumAttributeConverter(ButtonSize) },
      variant: {
         type: String,
         converter: enumAttributeConverter(ButtonVariant),
      },
      disabled: { type: Boolean },
      selectMultiple: { type: Boolean },
      fileUploadLabel: { type: String },
      folderUploadLabel: { type: String },
   };

   static styles = [fileUploadStyles];

   protected _handleOnDragLeave() {
      this.hoverClass = '';
   }

   protected _handleOnDragEnter() {
      this.hoverClass = 'hover';
   }

   protected async _handleOnDrop(event: DragEvent) {
      event.preventDefault();
      this.hoverClass = '';

      const items = event.dataTransfer?.items;
      if (!items) return;

      const files: File[] = [];
      const traversePromises: Promise<File[]>[] = [];

      for (let i = 0; i < items.length; i++) {
         const item = items[i];
         const entry = item.webkitGetAsEntry?.();
         if (entry) {
            traversePromises.push(DragDropReader.traverseFileTree(entry));
         }
      }

      const results = await Promise.all(traversePromises);
      results.forEach((arr) => files.push(...arr));

      if (files.length > 0) {
         const fileList = DragDropReader.filesArrayToFileList(files);
         this.dispatchEvent(new AtlFileUploadEvent(fileList));
      }
   }

   _renderAllowedFileTypes(): string {
      if (
         !this.allowedFileExtensions ||
         this.allowedFileExtensions.length === 0
      ) {
         return 'All file types';
      }
      if (this.allowedFileExtensions.length === 1) {
         return this.allowedFileExtensions[0];
      }
      if (this.allowedFileExtensions.length === 2) {
         return `${this.allowedFileExtensions[0]} and ${this.allowedFileExtensions[1]}`;
      }
      const allButLast = this.allowedFileExtensions.slice(0, -1).join(', ');
      const last =
         this.allowedFileExtensions[this.allowedFileExtensions.length - 1];
      return `${allButLast} and ${last}`;
   }

   _renderDragAndDropText = () =>
      this.foldersAllowed ? 'Drop a folder or file here' : 'Drop a file here';

   render() {
      return html`
         <section
            class="file-drop-zone ${this.hoverClass}"
            @dragenter=${this._handleOnDragEnter}
            @dragleave=${this._handleOnDragLeave}
            @dragover=${(event: DragEvent) => event.preventDefault()}
            @drop=${this._handleOnDrop}
         >
            <i>${unsafeSVG(publishIcon)}</i>

            <p class="drag-drop-heading">${this._renderDragAndDropText()}</p>
            <p>${this._renderAllowedFileTypes()} accepted</p>
            <p>or</p>
            <atlantis-file-upload-input
               .toggleLabel=${this.toggleLabel ?? 'Upload'}
               ?foldersAllowed=${this.foldersAllowed}
               .allowedFileExtensions=${this.allowedFileExtensions ?? []}
               ?selectMultiple=${this.selectMultiple ?? false}
               ?disabled=${this.disabled ?? false}
               size=${this.size ?? ButtonSize.Medium}
               variant=${this.variant ?? ButtonVariant.Secondary}
               .fileUploadLabel=${this.fileUploadLabel}
               .folderUploadLabel=${this.folderUploadLabel}
            >
            </atlantis-file-upload-input>
         </section>
      `;
   }
}
